/*
 * The product family in the sidebar, flat.
 *
 * There is no "Apps" bucket any more. With a handful of products, each area is a first-class
 * top-level item rather than a submenu under one "Apps" entry. This renders that cluster, in the
 * family's own order, from `products.ts` — so a product added there with an internal surface
 * appears here automatically, the same discipline the old "Apps" submenu kept.
 *
 * Graph is the one special case. Its surface is Backstage's `/catalog-graph`, and a bare link
 * there lands on an empty canvas — `CatalogGraphNavItem` builds the rooted URL (see that file for
 * why). So the Graph slot renders that component; every other product is a plain `SidebarItem` to
 * its route. Each carries its own product glyph rather than a borrowed Material icon, so the
 * cluster reads as one family.
 */
import { SidebarItem } from '@backstage/core-components';
import { PRODUCTS } from './products';
import { productNavItems } from './productNavModel';
import { ProductGlyph } from './ProductGlyph';
import { CatalogGraphNavItem } from '../nav/CatalogGraphNavItem';

/**
 * The product's own glyph, bound to a no-props component because `SidebarItem`'s `icon` takes
 * one. A product with no glyph renders an empty icon rather than a stand-in — see `ProductGlyph`.
 */
const glyphIconFor = (product: string): React.ComponentType =>
  function ProductGlyphIcon() {
    return <ProductGlyph product={product} size={20} />;
  };

export function ProductNav() {
  return (
    <>
      {productNavItems(PRODUCTS).map(product => {
        // Narrowed by productNavItems (available AND internal); the type system does not carry
        // that across the module boundary, so name the narrowing here rather than cast.
        if (product.surface.kind !== 'internal') return null;
        // Graph's link needs computed roots, not a bare /catalog-graph — see CatalogGraphNavItem.
        if (product.name === 'Graph') return <CatalogGraphNavItem key={product.name} />;
        const Icon = glyphIconFor(product.name);
        return (
          <SidebarItem
            key={product.name}
            icon={Icon}
            to={product.surface.path}
            text={product.name}
          />
        );
      })}
    </>
  );
}
